/**
 * Telemetry feature API layer — OpenAPI-first (§9.1, §9.3).
 */
import type {
  TelemetryChannelAggregatesParams,
  TelemetryChannelTimeseriesInterval,
  TelemetryChannelTimeseriesParams,
  TelemetryGroupTimeseriesInterval,
  TelemetryGroupTimeseriesParams,
} from "@shared/api/generated/model";
import {
  useTelemetryChannelAggregates as useGenAggregates,
  useTelemetryGroupTimeseries as useGenGroupTimeseries,
  useTelemetryChannelTimeseries as useGenTimeseries,
} from "@shared/api/generated/telemetry/telemetry";

export type {
  AggregatedPoint,
  GroupTelemetryMeta,
  GroupTimeseriesResponse,
  TimeseriesPoint,
} from "@shared/api/generated/model";

export const useTelemetryTimeseries = (params: {
  channelID?: number;
  from?: string;
  to?: string;
  interval?: number;
}) => {
  const enabled = Boolean(params.channelID && params.from && params.to);
  const p: TelemetryChannelTimeseriesParams = {
    from: params.from ?? "",
    to: params.to ?? "",
    ...(params.interval !== undefined && {
      interval: params.interval as TelemetryChannelTimeseriesInterval,
    }),
  };
  return useGenTimeseries(params.channelID ?? 0, p, {
    query: { enabled, staleTime: 60_000 },
  });
};

export const useTelemetryAggregates = (params: {
  channelID?: number;
  from?: string;
  to?: string;
  step?: number;
}) => {
  const enabled = Boolean(params.channelID && params.from && params.to);
  const p: TelemetryChannelAggregatesParams = {
    from: params.from ?? "",
    to: params.to ?? "",
    step: params.step ?? 60,
  };
  return useGenAggregates(params.channelID ?? 0, p, {
    query: { enabled, staleTime: 60_000 },
  });
};

export const useTelemetryGroupTimeseries = (params: {
  groupID?: string;
  from?: string;
  to?: string;
  interval?: number;
  includeStats?: boolean;
}) => {
  const enabled = Boolean(params.groupID && params.from && params.to);
  const p: TelemetryGroupTimeseriesParams = {
    from: params.from ?? "",
    to: params.to ?? "",
    ...(params.interval !== undefined && {
      interval: params.interval as TelemetryGroupTimeseriesInterval,
    }),
    ...(params.includeStats !== undefined && { include_stats: params.includeStats }),
  };
  return useGenGroupTimeseries(params.groupID ?? "", p, {
    query: { enabled, staleTime: 60_000 },
  });
};
